import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { bomClient } from '../../../client/bomClient';
import { useRFQ } from '../../../contexts/RFQContext';
import { BomCompleteness } from '../../../types/bom.types';
import { ManualEditRequest } from '../../../types/bomVersions';
import { apiErrorMessage } from '../../../utils/apiErrorMessage';
import { useRecordRobbieAction } from '../../../contexts/RobbieUiContext';
import { robbieActionText } from '../../../utils/robbieActivity';
import { BomSaveFailure, ExtractedBomRow } from './types';

/**
 * Where the step-2 sheet's data comes from and where its Save goes.
 *
 * - Lines: `rfqData.analysisData` (the RFQ read — the latest BOM only — or,
 *   right after an upload, the upload response).
 * - BOM id: the upload response carries `bomId`; the RFQ read does not, so it
 *   is the `boms[]` entry `BOM.getLatestVersion` would pick (highest version,
 *   then newest) — the same BOM `analysisData` was built from.
 * - Completeness: the upload response's own, else the RFQ read's.
 *
 * Save → manual edit (`source: 'buyer'`) → silent RFQ refresh that keeps the
 * wizard where it is and leaves the buyer's unsaved criteria alone, so
 * `analysisData` and `completeness` come back fresh.
 */

/** The slice of the RFQ payload (or upload response) the sheet reads. */
interface BomAnalysis {
  components?: ExtractedBomRow[];
  completeness?: BomCompleteness;
  bomId?: string;
}
interface BomSheetSource {
  rfqId?: string;
  analysisData?: BomAnalysis | null;
  extractedData?: BomAnalysis | null;
  completeness?: BomCompleteness | null;
  boms?: Array<{ bomId?: string; version?: number; createdAt?: string }>;
}

export interface BomSaveNotice {
  status: 'success' | 'neutral';
  title: string;
  message: string;
}

const latestBomId = (boms: BomSheetSource['boms']): string | null => {
  if (!Array.isArray(boms) || boms.length === 0) return null;
  const newest = [...boms].sort(
    (a, b) =>
      (b?.version ?? 0) - (a?.version ?? 0) ||
      new Date(b?.createdAt ?? 0).getTime() - new Date(a?.createdAt ?? 0).getTime()
  )[0];
  return newest?.bomId ?? null;
};

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** The manual-edit 400 → what went wrong and, when it says, which line. */
export const toFailure = (error: unknown): BomSaveFailure => {
  // BaseClient already put the server's sentence on error.message.
  const raw = apiErrorMessage(error, "the server didn't say why");
  const blamed = (error as { response?: { data?: { componentId?: unknown } } })?.response?.data?.componentId;
  const position = raw.match(/^(adds|components)\[(\d+)\]:\s*/);
  return {
    message: position ? raw.slice(position[0].length) : raw,
    componentId: typeof blamed === 'string' ? blamed : undefined,
    addIndex: position?.[1] === 'adds' ? Number(position[2]) : undefined,
  };
};

export const useExtractedBomSave = () => {
  const rfqData = useSelector((state: RootState) => state.rfq.rfqData as unknown as BomSheetSource | null);
  const { fetchRFQ } = useRFQ();
  const recordAction = useRecordRobbieAction();
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<BomSaveNotice | null>(null);

  const analysis = rfqData?.analysisData || rfqData?.extractedData;
  const components: ExtractedBomRow[] = analysis?.components || [];
  const completeness: BomCompleteness | null = analysis?.completeness ?? rfqData?.completeness ?? null;
  const bomId: string | null = analysis?.bomId ?? latestBomId(rfqData?.boms);
  const rfqId: string | undefined = rfqData?.rfqId;

  const save = useCallback(
    async (request: ManualEditRequest, changedCount: number): Promise<BomSaveFailure | null> => {
      if (!bomId) {
        return { message: "the BOM behind this sheet hasn't loaded" };
      }
      setSaving(true);
      setNotice(null);
      try {
        const result = await bomClient.manualEditBom(bomId, request);
        if (!result.noChanges) {
          recordAction('editBom', robbieActionText.editedBom(changedCount, result.added.length, result.removed.length));
        }
        // keepCriteria: unsaved step-2 criteria must survive a BOM edit.
        if (rfqId) await fetchRFQ(rfqId, true, true, true);
        if (result.noChanges) {
          setNotice({
            status: 'neutral',
            title: 'Nothing to save',
            message: "Those values match what's already on the BOM, so I didn't record a new version.",
          });
        } else {
          const parts = [
            plural(result.components.length, 'line'),
            changedCount > 0 && `${changedCount} changed`,
            result.added.length > 0 && `${result.added.length} added`,
            result.removed.length > 0 && `${result.removed.length} removed`,
          ].filter(Boolean);
          setNotice({ status: 'success', title: 'Saved.', message: `${parts.join(', ')}.` });
        }
        return null;
      } catch (error) {
        return toFailure(error);
      } finally {
        setSaving(false);
      }
    },
    [bomId, rfqId, fetchRFQ, recordAction]
  );

  const dismissNotice = useCallback(() => setNotice(null), []);

  return { components, completeness, bomId, saving, save, notice, dismissNotice };
};

export default useExtractedBomSave;
